import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import { Input, cn } from '@nw/ui';
import { api, useMe } from '@nw/web-platform';
import type { Person } from '@nw/contracts';
import type { Book } from '../public/index.js';
import { searchConcepts } from './api.js';

/** Real cover when available; otherwise a designed typographic cover, marked as such. */
export function BookCover({ book, className, layout = true }: { book: Pick<Book, 'id' | 'title' | 'authors' | 'cover' | 'spine'>; className?: string; layout?: boolean }) {
  const [failed, setFailed] = useState(false);
  const Comp = layout ? motion.div : 'div';
  const props = layout
    ? {
        layoutId: `book-${book.id}`,
        transition: { type: 'spring' as const, stiffness: 260, damping: 30 },
      }
    : {};
  return (
    <Comp {...props} className={cn('relative aspect-[2/3] overflow-hidden rounded-[4px] shadow-[0_18px_40px_-18px_rgb(0_0_0/0.5)]', className)} style={{ background: book.spine.color, color: book.spine.ink }}>
      {book.cover.url && !failed ? (
        <img src={book.cover.url} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setFailed(true)} className="absolute inset-0 size-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col justify-between p-[9%]">
          <span aria-hidden className="h-px w-1/3 bg-current opacity-50" />
          <span className="font-display line-clamp-5 text-[clamp(0.95rem,1.6vw,1.35rem)] leading-tight font-medium">{book.title}</span>
          <span className="text-[0.7rem] font-semibold">{book.authors.map((a) => a.name).join(', ')}</span>
        </div>
      )}
      <span aria-hidden className="absolute inset-y-0 left-0 w-[6%] bg-gradient-to-r from-black/25 to-transparent" />
    </Comp>
  );
}

export type AuthorChoice = { personId: string; name: string } | { name: string; providerId?: string | null };

/** Authors are shared people. Choosing an existing person is explicit; names never auto-merge. */
export function AuthorsInput({ value, onChange, id }: { value: AuthorChoice[]; onChange: (v: AuthorChoice[]) => void; id?: string }) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<Person[]>([]);
  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) return setSuggestions([]);
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api<Person[]>(`/people?q=${encodeURIComponent(q)}`, {
        signal: ctrl.signal,
      })
        .then((r) => setSuggestions(r.data))
        .catch(() => undefined);
    }, 180);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [text]);
  const add = (a: AuthorChoice) => {
    onChange([...value, a]);
    setText('');
    setSuggestions([]);
  };
  return (
    <div>
      {value.length ? (
        <ul className="mb-2 flex flex-wrap gap-2">
          {value.map((a, i) => (
            <li key={i} className="flex h-9 items-center gap-1 rounded-full border border-[var(--nw-line-strong)] ps-3 pe-1 text-sm">
              {a.name}
              {'personId' in a ? (
                <span className="text-xs text-[var(--nw-quiet)]">(existing)</span>
              ) : 'providerId' in a && a.providerId ? (
                <span className="text-xs text-[var(--nw-quiet)]">(Open Library)</span>
              ) : (
                <span className="text-xs text-[var(--nw-quiet)]">(new)</span>
              )}
              <button type="button" aria-label={`Remove ${a.name}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="grid size-8 place-items-center rounded-full hover:bg-[var(--nw-surface-sunk)]">
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <Input
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim()) {
            e.preventDefault();
            add({ name: text.trim() });
          }
        }}
        placeholder="Type a name and press Enter"
        autoComplete="off"
      />
      {suggestions.length ? (
        <div className="mt-2 rounded-[var(--nw-radius-field)] border border-[var(--nw-line)] p-1">
          <p className="px-2 py-1 text-xs text-[var(--nw-quiet)]">Already in your workspace. Choose one to reuse that person, or press Enter to add a new person.</p>
          {suggestions.map((p) => (
            <button key={p.id} type="button" onClick={() => add({ personId: p.id, name: p.name })} className="block min-h-10 w-full rounded px-2 text-start text-sm hover:bg-[var(--nw-surface-sunk)]">
              {p.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Optional topic links through Mind Networks' public capability. */
export function ConceptPicker({ value, onChange, labels }: { value: string[]; onChange: (v: string[]) => void; labels: Record<string, string> }) {
  const me = useMe();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Array<{ key: string; title: string }>>([]);
  const [known, setKnown] = useState<Record<string, string>>(labels);
  const enabled = me.data?.enabledModules.includes('mind-networks');
  useEffect(() => setKnown((k) => ({ ...labels, ...k })), [labels]);
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      searchConcepts(q)
        .then(setResults)
        .catch(() => setResults([]));
    }, 180);
    return () => clearTimeout(t);
  }, [q, enabled]);
  if (!enabled) return <p className="text-sm text-[var(--nw-quiet)]">Topic links need Mind Networks, which is turned off. Notes still save normally.</p>;
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {value.map((k) => (
          <span key={k} className="flex h-9 items-center gap-1 rounded-full bg-[var(--nw-river-soft)] ps-3 pe-1 text-sm">
            {known[k] ?? 'Idea'}
            <button type="button" aria-label={`Remove topic ${known[k] ?? ''}`} onClick={() => onChange(value.filter((v) => v !== k))} className="grid size-8 place-items-center rounded-full hover:bg-black/5">
              <X className="size-3.5" />
            </button>
          </span>
        ))}
      </div>
      <Input className="mt-2" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an idea from Mind Networks" aria-label="Find an idea" />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(q.trim() ? results : [])
          .filter((r) => !value.includes(r.key))
          .slice(0, 8)
          .map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => {
                setKnown((k) => ({ ...k, [r.key]: r.title }));
                onChange([...value, r.key]);
              }}
              className="h-9 rounded-full border border-[var(--nw-line-strong)] px-3 text-sm hover:border-[var(--nw-river)]"
            >
              + {r.title}
            </button>
          ))}
        {q.trim() && results.length === 0 ? <span className="text-sm text-[var(--nw-quiet)]">No idea with that name. Create it in Mind Networks.</span> : null}
      </div>
    </div>
  );
}

export function useConceptLabels(keys: string[]) {
  const [labels, setLabels] = useState<Record<string, string>>({});
  const me = useMe();
  const sig = keys.join(',');
  useEffect(() => {
    if (!keys.length || !me.data?.enabledModules.includes('mind-networks')) return;
    Promise.all(
      keys.map((k) =>
        api<{ entity: { title: string } }>(`/entities/${encodeURIComponent(k)}`)
          .then((r) => [k, r.data.entity.title] as const)
          .catch(() => null),
      ),
    )
      .then((rows) => setLabels(Object.fromEntries(rows.filter(Boolean) as Array<readonly [string, string]>)))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, me.data?.enabledModules]);
  return labels;
}

export function ProgressLine({ book, className }: { book: Pick<Book, 'pageCount' | 'reading'>; className?: string }) {
  const page = book.reading?.currentPage;
  const pdf = book.reading?.pdfPageIndex;
  const pct = page != null && book.pageCount ? Math.min(100, Math.round((page / book.pageCount) * 100)) : null;
  return (
    <div className={className}>
      <p className="text-sm text-[var(--nw-quiet)]">
        {page != null ? (book.pageCount ? `Page ${page} of ${book.pageCount}` : `Page ${page}, total pages unknown`) : book.pageCount ? `${book.pageCount} pages, not started` : 'Not started'}
        {pdf != null ? `. PDF at page ${pdf + 1}` : ''}
      </p>
      {pct != null ? (
        <div className="mt-2 h-[3px] rounded-full bg-[var(--nw-line)]" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Reading progress">
          <div className="h-full rounded-full bg-[var(--nw-river)]" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}
